import React from 'react';
import { 
  ChevronLeft, Truck, Package, CheckCircle, 
  Clock, CreditCard, User, Weight, DollarSign, 
  FileText, Phone, Navigation
} from 'lucide-react';

const DELIVERY_STATUS_CONFIG = {
  '待送货': { color: 'bg-slate-500', icon: Clock, bgColor: 'bg-slate-50', borderColor: 'border-slate-200' },
  '已到场': { color: 'bg-blue-500', icon: Truck, bgColor: 'bg-blue-50', borderColor: 'border-blue-200' },
  '已入库': { color: 'bg-purple-500', icon: Package, bgColor: 'bg-purple-50', borderColor: 'border-purple-200' },
  '已结算': { color: 'bg-amber-500', icon: DollarSign, bgColor: 'bg-amber-50', borderColor: 'border-amber-200' },
  '已付款': { color: 'bg-green-500', icon: CreditCard, bgColor: 'bg-green-50', borderColor: 'border-green-200' },
};

const STATUS_ORDER = ['待送货', '已到场', '已入库', '已结算', '已付款'];

const MOCK_ORDER = {
  orderNo: 'ORD20260218001',
  status: '送货中',
  baseName: '马钢诚兴马鞍山基地',
  material: '重型废钢 (HMS1)',
  estimatedWeight: 50,
  deliveries: [
    { 
      id: 'D001', 
      deliveryNo: 'DEL20260218001', 
      status: '已付款', 
      weight: 10.2, 
      amount: 27030, 
      vehicleNo: '皖E12345', 
      driverName: '张师傅', 
      driverPhone: '138****1234',
      documents: ['预约单', '到场单', '入库单', '结算单', '付款凭证']
    },
    { 
      id: 'D002', 
      deliveryNo: 'DEL20260218002', 
      status: '已入库', 
      weight: 12.3, 
      vehicleNo: '皖E23456', 
      driverName: '李师傅', 
      driverPhone: '139****5678',
      documents: ['预约单', '到场单', '入库单']
    },
    { 
      id: 'D003', 
      deliveryNo: 'DEL20260218003', 
      status: '已到场', 
      vehicleNo: '皖E34567', 
      driverName: '王师傅', 
      driverPhone: '137****9012',
      documents: ['预约单', '到场单']
    },
  ]
};


const DeliveryCard = ({ delivery, index, total }) => {
  const config = DELIVERY_STATUS_CONFIG[delivery.status];
  const Icon = config.icon;
  const currentStatusIndex = STATUS_ORDER.indexOf(delivery.status);
  
  return (
    <div className={`rounded-xl border ${config.borderColor} ${config.bgColor} overflow-hidden mb-3`}>
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl ${config.color} flex items-center justify-center`}>
              <Icon size={24} className="text-white" />
            </div>
            <div>
              <div className="font-bold text-slate-800">{delivery.deliveryNo}</div>
              <div className="text-xs text-slate-500">批次 {index + 1}/{total}</div>
            </div>
          </div>
          <div className="text-right">
            <span className={`text-sm font-bold ${config.color.replace('bg-', 'text-').replace('-500', '-600')}`}>
              {delivery.status}
            </span>
            {delivery.amount && (
              <div className="text-sm font-bold text-red-600 mt-1">
                ¥{delivery.amount.toLocaleString()}
              </div>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-3 mb-3 text-xs text-slate-600">
          {delivery.weight && (
            <div className="flex items-center gap-1 bg-white/60 px-2 py-1 rounded">
              <Weight size={12} />
              {delivery.weight}吨
            </div>
          )}
          {delivery.vehicleNo && (
            <div className="flex items-center gap-1 bg-white/60 px-2 py-1 rounded">
              <Truck size={12} />
              {delivery.vehicleNo}
            </div>
          )}
          {delivery.driverName && (
            <div className="flex items-center gap-1 bg-white/60 px-2 py-1 rounded">
              <User size={12} />
              {delivery.driverName}
            </div>
          )}
        </div>
        
        <div className="grid grid-cols-5 gap-1.5">
          {STATUS_ORDER.map((status, i) => {
            const completed = i <= currentStatusIndex;
            const isCurrent = i === currentStatusIndex;
            const statusConfig = DELIVERY_STATUS_CONFIG[status];
            
            return (
              <div
                key={status}
                className={`py-2 px-1 rounded-lg text-center transition-all ${
                  completed 
                    ? `${isCurrent ? statusConfig.color + ' text-white' : 'bg-green-100 text-green-700'}` 
                    : 'bg-white/40 text-slate-400'
                }`}
              >
                <div className="text-[10px] font-medium">{status}</div>
                {completed && <CheckCircle size={10} className="mx-auto mt-0.5" />}
              </div>
            );
          })}
        </div>
        
        <div className="mt-3 pt-3 border-t border-slate-200/50">
          <div className="text-[10px] font-bold text-slate-500 mb-2">快捷操作</div>
          <div className="grid grid-cols-3 gap-2">
            <button className="flex items-center justify-center gap-1 py-2 bg-white rounded-lg text-xs text-slate-600 border border-slate-200">
              <FileText size={12} />
              查看单据
            </button>
            <button className="flex items-center justify-center gap-1 py-2 bg-white rounded-lg text-xs text-slate-600 border border-slate-200">
              <Phone size={12} />
              联系司机
            </button>
            <button className="flex items-center justify-center gap-1 py-2 bg-white rounded-lg text-xs text-slate-600 border border-slate-200">
              <Navigation size={12} />
              导航
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function OrderDetailPrototype4({ onBack }) {
  const order = MOCK_ORDER;
  
  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="flex items-center justify-between px-4 py-3">
          <button onClick={onBack} className="flex items-center gap-1 text-slate-600">
            <ChevronLeft size={20} />
            <span>返回</span>
          </button>
          <h1 className="font-bold text-slate-800">订单详情</h1>
          <div className="w-16" />
        </div>
      </div>
      
      <div className="p-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 mb-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="font-bold text-slate-800">{order.orderNo}</div>
              <div className="text-xs text-slate-500 mt-1">{order.baseName}</div>
            </div>
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-500 text-white">
              {order.status}
            </span>
          </div>
          
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <Package size={12} className="text-slate-400" />
            <span>{order.material}</span>
            <span className="text-slate-300">|</span>
            <Weight size={12} className="text-slate-400" />
            <span>预估 {order.estimatedWeight}吨</span>
          </div>
        </div>
        
        <div className="mb-3">
          <h2 className="font-bold text-slate-800">送货单列表</h2>
          <p className="text-xs text-slate-500">状态一目了然，操作触手可及</p>
        </div>
        
        {order.deliveries.map((delivery, index) => (
          <DeliveryCard 
            key={delivery.id} 
            delivery={delivery} 
            index={index} 
            total={order.deliveries.length} 
          />
        ))}
      </div>
      
      <div className="fixed bottom-16 left-0 right-0 bg-white border-t border-slate-200 p-4">
        <button className="w-full bg-blue-500 text-white py-3 rounded-xl font-bold">
          新增送货单
        </button>
      </div>
    </div>
  );
}